'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface AppImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    priority?: boolean;
    quality?: number;
    placeholder?: 'blur' | 'empty';
    blurDataURL?: string;
    fill?: boolean;
    sizes?: string;
    onClick?: () => void;
    fallbackSrc?: string;
    monochrome?: boolean; // Display image in grayscale
    noLoadingBg?: boolean; // Disable loading background color
    objectPosition?: string;
    [key: string]: unknown;
}

/** Responsive sizes hint: mobile full-width, tablet half, desktop third */
const DEFAULT_SIZES = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';

function AppImage({
    src,
    alt = '',
    width,
    height,
    className = '',
    priority = false,
    quality = 60,
    placeholder = 'empty',
    blurDataURL,
    fill = false,
    sizes,
    onClick,
    fallbackSrc = '/assets/images/no_image.png',
    monochrome = false,
    noLoadingBg = false,
    objectPosition,
    ...props
}: AppImageProps) {
    const [imageSrc, setImageSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // Data URLs cannot be processed by the Next.js image optimizer
    const isDataUrl = imageSrc.startsWith('data:');

    const handleError = () => {
        if (!hasError && imageSrc !== fallbackSrc) {
            setImageSrc(fallbackSrc);
            setHasError(true);
        }
        setIsLoading(false);
    };

    const handleLoad = () => {
        setIsLoading(false);
        setHasError(false);
    };

    const monochromeClass = monochrome ? 'grayscale' : '';

    const commonClassName = `${className} ${monochromeClass} ${isLoading && !noLoadingBg ? 'bg-gray-200' : ''} ${
        onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
    }`;

    // Shared props for all rendering modes
    const imageProps = {
        src: imageSrc,
        className: commonClassName,
        priority,
        quality,
        placeholder: (blurDataURL ? placeholder : 'empty') as 'blur' | 'empty',
        blurDataURL,
        unoptimized: isDataUrl,
        onError: handleError,
        onLoad: handleLoad,
        onClick,
        ...props,
    };

    if (fill) {
        return (
            <Image data-component-id="src/components/ui/AppImage.tsx:91:12" data-component-path="src/components/ui/AppImage.tsx" data-component-line="91" data-component-end-line="97" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%7D"
                {...imageProps}
                alt={alt}
                fill
                sizes={sizes || DEFAULT_SIZES}
                style={{ objectFit: 'cover', objectPosition }}
            />
        );
    }

    return (
        <Image data-component-id="src/components/ui/AppImage.tsx:102:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="102" data-component-end-line="109" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22%5Bspread%5D%22%3A%22true%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%7D"
            {...imageProps}
            alt={alt}
            width={width || 800}
            height={height || 600}
            sizes={sizes || DEFAULT_SIZES}
            style={{ objectPosition, ...(imageProps as { style?: React.CSSProperties }).style }}
        />
    );
}

export default AppImage;
